import { Internal, OHIF } from '@gleamer/types';
import sortBy from 'lodash/sortBy';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isReadonly, labelPass, useObservationSubmissions, useTask } from '@gleamer/ui';
import { useTranslation } from 'react-i18next';
import { missingFieldsCount } from '../../../../../utils/validation.utils';
import { ReportMentions } from '../../report/ReportMentions';
import ObservationItem from './ObservationItem';
import classNames from 'classnames';
const { setLastUpdated } = labelPass.actions;

type ServicesManager = OHIF.ServicesManager;
type ObservationSubmission = Internal.ObservationInternal;

type ObservationsListProps = {
  servicesManager: ServicesManager;
};

function ObservationsList({ servicesManager }: ObservationsListProps) {
  const observations = useObservationSubmissions();
  const lastUpdatedObsId = useSelector(
    labelPass.selectors.getLastUpdatedObsUid
  );
  const dispatch = useDispatch();

  const onObservationClick = useCallback(
    (observation: ObservationSubmission) => {
      return () => {
        dispatch(setLastUpdated({ observation }));
      };
    },
    [dispatch]
  );

  if (observations.length === 0) {
    return (
      <div className="flex grow cursor-default px-4 py-3 text-sm text-dl-muted">
        No observation
      </div>
    );
  }

  return (
    <ul className="ohif-scrollbar max-h-full min-h-0 grow overflow-auto py-1">
      {sortBy(observations, 'code').map(observation => (
        <li key={observation.uid}>
          <ObservationItem
            servicesManager={servicesManager}
            index={observation.index}
            observation={observation}
            open={observation.uid === lastUpdatedObsId}
            onObservationClick={onObservationClick(observation)}
          />
        </li>
      ))}
    </ul>
  );
}

type ObservationsProps = {
  servicesManager: ServicesManager;
  className?: string;
};

export function Observations({
  servicesManager,
  className,
}: ObservationsProps) {
  return (
    <div
      className={classNames(
        'observation-panel characterisation-panel flex flex-auto flex-col overflow-hidden',
        className
      )}
      data-testid="annotation-panel"
    >
      <ReportMentions />
      <div className="flex grow flex-col overflow-auto">
        <ObservationsList servicesManager={servicesManager} />
      </div>
      <RequiredFieldsFooter />
    </div>
  );
}

/**
 * The design's footer: every lesion that still lacks required fields, with how many — amber, each name opening its
 * lesion — or, once every lesion is complete, that it is done — green. Nothing while there is no lesion.
 */
export function RequiredFieldsFooter() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const task = useTask();
  const observations = useObservationSubmissions();
  if (!task?.observations || observations.length === 0 || isReadonly()) {
    return null;
  }
  const incomplete = sortBy(observations, 'code')
    .map(observation => ({ observation, missing: missingFieldsCount(task, observation) }))
    .filter(({ missing }) => missing > 0);
  const total = incomplete.reduce((sum, { missing }) => sum + missing, 0);
  const nameOf = (observation: ObservationSubmission) => {
    const label = task.observations.find(spec => spec.code === observation.code && spec.status === observation.status)?.label;
    return `${label ?? observation.code} ${observation.index}`;
  };
  const lesionLink = ({ observation, missing }: { observation: ObservationSubmission; missing: number }, withCount: boolean) => (
    <button
      key={observation.uid}
      type="button"
      className="font-medium text-dl-text underline decoration-dl-strong underline-offset-2 hover:decoration-dl-text"
      onClick={() => dispatch(setLastUpdated({ observation }))}
    >
      {nameOf(observation)}
      {withCount && <span className="font-normal text-dl-muted"> ({missing})</span>}
    </button>
  );
  return (
    <div
      className="flex shrink-0 items-start gap-2.5 border-t border-dl-border px-4 py-3 text-[13px] text-dl-muted"
      role="status"
      data-testid="required-fields"
    >
      <span aria-hidden className={classNames('mt-[5px] h-2 w-2 shrink-0 rounded-full', incomplete.length ? 'bg-dl-warning' : 'bg-dl-success')} />
      {incomplete.length === 0 && <span>{t('All required fields done on the lesions.')}</span>}
      {incomplete.length === 1 && (
        <span>
          {t(total === 1 ? '1 required field left on' : '{{count}} required fields left on', { count: total })}{' '}
          {lesionLink(incomplete[0], false)}
        </span>
      )}
      {incomplete.length > 1 && (
        <span>
          {t('{{count}} required fields left on {{lesions}} lesions:', { count: total, lesions: incomplete.length })}{' '}
          {incomplete.map((entry, i) => (
            <React.Fragment key={entry.observation.uid}>
              {i > 0 && ', '}
              {lesionLink(entry, true)}
            </React.Fragment>
          ))}
        </span>
      )}
    </div>
  );
}
